// ─── Shared rich-text model for PDF + Word exports ─────────────────────────────
// Parses TipTap sermon HTML into a normalized intermediate representation (IR)
// that both the PDF (jsPDF) and Word (docx) renderers consume. This is what
// lets exports preserve highlights, colors, bold/italic/underline, lists,
// headings and blockquotes instead of flattening to plain text.

export type RGB = [number, number, number];

// A single styled span of text within a block.
export interface InlineRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;      // 6-char hex, no leading '#'
  highlight?: string;  // 6-char hex, no leading '#'
  link?: string;
}

export type BlockKind = 'paragraph' | 'heading' | 'bullet' | 'ordered' | 'blockquote';

// A block-level element (paragraph, heading, list item, quote).
export interface RichBlock {
  kind: BlockKind;
  level: number;                 // heading level 1-3, or list nesting depth (0-based)
  ordinal?: number;              // 1-based number for ordered list items
  align?: 'left' | 'center' | 'right' | 'justify';
  runs: InlineRun[];
}

// A top-level sermon element: either a typed section or loose content.
export interface SermonElement {
  type: 'section' | 'content';
  sectionType?: string;
  pointNumber?: string;
  attribution?: string;
  customTitle?: string;
  content: string;               // raw HTML (section innerHTML, or element outerHTML)
}

export interface SermonMeta {
  title?: string;
  scripture?: string;
  date?: string;
}

// ─── Brand / section colors (mirrors tailwind.config.js + useSectionStyles) ─────
export const SECTION_HEX: Record<string, string> = {
  scripture:    'B22A2F', // crimson
  illustration: '2D7F5E', // emerald
  quote:        '2874A6', // electric
  application:  'D9A441', // gold
  'main-point': 'E86A33', // fiery
  custom:       '4F5D75', // slate
};
export const DEFAULT_SECTION_HEX = '4F5D75';

export function sectionHex(type: string | undefined): string {
  return (type && SECTION_HEX[type]) || DEFAULT_SECTION_HEX;
}

export function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(rgb: RGB): string {
  return rgb.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

// Mix a color toward white. amount 0 = original, 1 = white.
export function tint(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount]);
}

export function titleCase(type: string): string {
  return type
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function sectionLabel(el: SermonElement): string {
  const t = el.sectionType || '';
  if (t === 'custom' && el.customTitle) return el.customTitle;
  if (t === 'main-point') return el.pointNumber ? `Main Point ${el.pointNumber}` : 'Main Point';
  return titleCase(t || 'Section');
}

// ─── CSS color parsing ──────────────────────────────────────────────────────
// Accepts '#rgb', '#rrggbb', 'rgb(r,g,b)' / 'rgba(...)' and returns 6-char hex.
function cssColorToHex(input: string | null | undefined): string | undefined {
  if (!input) return undefined;
  const s = input.trim();
  if (!s || s === 'inherit' || s === 'transparent' || s === 'currentcolor') return undefined;
  if (s.startsWith('#')) {
    const h = s.slice(1);
    if (h.length === 3) return h.split('').map(c => c + c).join('').toLowerCase();
    if (h.length === 6) return h.toLowerCase();
    return undefined;
  }
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const a = s.match(/rgba?\([^)]*,\s*([\d.]+)\s*\)/i);
    if (a && parseFloat(a[1]) === 0) return undefined; // fully transparent
    return rgbToHex([parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)]);
  }
  return undefined;
}

// ─── Inline parsing ───────────────────────────────────────────────────────────
type Marks = Omit<InlineRun, 'text'>;

function collectNode(node: ChildNode, marks: Marks, out: InlineRun[]): void {
  if (node.nodeType === 3 /* TEXT_NODE */) {
    const raw = node.textContent || '';
    if (!raw) return;
    const text = raw.replace(/\s+/g, ' ');
    if (text) out.push({ text, ...marks });
    return;
  }
  if (node.nodeType !== 1 /* ELEMENT_NODE */) return;

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') {
    out.push({ text: '\n', ...marks });
    return;
  }

  const next: Marks = { ...marks };
  switch (tag) {
    case 'strong':
    case 'b':
      next.bold = true;
      break;
    case 'em':
    case 'i':
      next.italic = true;
      break;
    case 'u':
    case 'ins':
      next.underline = true;
      break;
    case 's':
    case 'strike':
    case 'del':
      next.strike = true;
      break;
    case 'mark': {
      next.highlight =
        cssColorToHex(el.style?.backgroundColor) ||
        cssColorToHex(el.getAttribute('data-color')) ||
        'fef08a'; // default soft yellow
      break;
    }
    case 'a': {
      const href = el.getAttribute('href');
      if (href) next.link = href;
      next.underline = true;
      if (!next.color) next.color = '2874A6';
      break;
    }
    case 'sup':
    case 'sub':
      // treated as normal inline text; positioning not preserved
      break;
  }

  // Inline style colors from TextStyle/Color/Highlight spans on any element.
  const fg = cssColorToHex(el.style?.color);
  if (fg) next.color = fg;
  if (tag !== 'mark') {
    const bg = cssColorToHex(el.style?.backgroundColor);
    if (bg) next.highlight = bg;
  }

  el.childNodes.forEach(child => collectNode(child, next, out));
}

function inlineRunsOf(el: HTMLElement, skipLists = false): InlineRun[] {
  const out: InlineRun[] = [];
  el.childNodes.forEach(child => {
    if (skipLists && child.nodeType === 1) {
      const t = (child as HTMLElement).tagName.toLowerCase();
      if (t === 'ul' || t === 'ol') return;
    }
    collectNode(child, {}, out);
  });
  return normalizeRuns(out);
}

// Merge adjacent runs with identical marks; drop empties.
function normalizeRuns(runs: InlineRun[]): InlineRun[] {
  const out: InlineRun[] = [];
  for (const r of runs) {
    if (!r.text) continue;
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.bold === r.bold &&
      prev.italic === r.italic &&
      prev.underline === r.underline &&
      prev.strike === r.strike &&
      prev.color === r.color &&
      prev.highlight === r.highlight &&
      prev.link === r.link
    ) {
      prev.text += r.text;
    } else {
      out.push({ ...r });
    }
  }
  return out;
}

function hasText(runs: InlineRun[]): boolean {
  return runs.some(r => r.text.trim().length > 0);
}

// ─── Block parsing ──────────────────────────────────────────────────────────
function processList(listEl: HTMLElement, blocks: RichBlock[], level: number): void {
  const ordered = listEl.tagName.toLowerCase() === 'ol';
  let ordinal = 1;
  Array.from(listEl.children).forEach(child => {
    if (child.tagName.toLowerCase() !== 'li') return;
    const li = child as HTMLElement;
    const runs = inlineRunsOf(li, true);
    if (hasText(runs)) {
      blocks.push({
        kind: ordered ? 'ordered' : 'bullet',
        level,
        ordinal: ordered ? ordinal : undefined,
        runs,
      });
    }
    if (ordered) ordinal++;
    // Nested lists
    Array.from(li.children).forEach(sub => {
      const t = sub.tagName.toLowerCase();
      if (t === 'ul' || t === 'ol') processList(sub as HTMLElement, blocks, level + 1);
    });
  });
}

function walkBlocks(parent: HTMLElement, blocks: RichBlock[]): void {
  parent.childNodes.forEach(node => {
    if (node.nodeType === 3) {
      const t = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (t) blocks.push({ kind: 'paragraph', level: 0, runs: [{ text: t }] });
      return;
    }
    if (node.nodeType !== 1) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const align = (el.style?.textAlign || undefined) as RichBlock['align'];

    switch (tag) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const runs = inlineRunsOf(el);
        if (hasText(runs)) {
          blocks.push({ kind: 'heading', level: Math.min(3, parseInt(tag[1], 10)), align, runs });
        }
        break;
      }
      case 'ul':
      case 'ol':
        processList(el, blocks, 0);
        break;
      case 'blockquote': {
        const runs = inlineRunsOf(el);
        if (hasText(runs)) blocks.push({ kind: 'blockquote', level: 0, align, runs });
        break;
      }
      case 'p':
      case 'div': {
        const runs = inlineRunsOf(el, true);
        if (hasText(runs)) {
          blocks.push({ kind: 'paragraph', level: 0, align, runs });
        }
        // Handle lists nested directly inside a wrapper div/p.
        el.querySelectorAll(':scope > ul, :scope > ol').forEach(l =>
          processList(l as HTMLElement, blocks, 0)
        );
        break;
      }
      default: {
        // Unknown wrapper: recurse so we don't lose content.
        walkBlocks(el, blocks);
      }
    }
  });
}

export function htmlToBlocks(html: string): RichBlock[] {
  if (!html) return [];
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const blocks: RichBlock[] = [];
  walkBlocks(body, blocks);
  return blocks;
}

// ─── Sermon HTML → element list (sections + loose content) ─────────────────────
// Mirrors the processContent() logic in PreachMode/SharedSermon so the Editor
// export can reuse the same pipeline. No visibility filtering here.
export function parseSermonContent(html: string): SermonElement[] {
  if (!html) return [];
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return Array.from(body.children).map(el => {
    if (el.classList.contains('sermon-section')) {
      const sectionType = Array.from(el.classList)
        .find(c => c.startsWith('sermon-section-'))
        ?.replace('sermon-section-', '');
      return {
        type: 'section' as const,
        sectionType: sectionType || '',
        pointNumber: el.getAttribute('data-point-number') || '',
        attribution: el.getAttribute('data-attribution') || '',
        customTitle: el.getAttribute('data-custom-title') || '',
        content: el.innerHTML,
      };
    }
    return { type: 'content' as const, content: el.outerHTML };
  });
}

export function formatExportDate(date?: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(date);
  if (isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function sanitizeFilename(name: string | undefined, fallback = 'sermon'): string {
  const base = (name || fallback).replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase();
  return base || fallback;
}
